'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { AuthStatusIndicator } from '@/components/auth/AuthStatusIndicator';
import { ChevronDown, Menu, X, Shield, Settings, Search, Home, BookOpen, Users, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { debounce } from 'lodash';

// Constantes de navigation
const NAV_ITEMS = {
  produit: [
    { name: 'Fonctionnalités', href: '/features', icon: <Shield className="h-4 w-4" /> },
    { name: 'Tarification', href: '/pricing', icon: <Settings className="h-4 w-4" /> },
    { name: 'Démo', href: '/demo', icon: <Search className="h-4 w-4" /> },
  ],
  ressources: [
    { name: 'Documentation', href: '/docs', icon: <BookOpen className="h-4 w-4" /> },
    { name: 'Guides', href: '/guides', icon: <Home className="h-4 w-4" /> },
    { name: 'Support', href: '/support', icon: <Users className="h-4 w-4" /> },
  ],
  entreprise: [
    { name: 'À propos', href: '/about', icon: <Users className="h-4 w-4" /> },
    { name: 'Contact', href: '/contact', icon: <Mail className="h-4 w-4" /> },
    { name: 'Blog', href: '/blog', icon: <BookOpen className="h-4 w-4" /> },
  ],
};

// Interface pour les props du dropdown
interface DropdownProps {
  title: string;
  items: { name: string; href: string; icon?: React.ReactNode }[];
  isMobile?: boolean;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
}

// Composant Dropdown
const NavDropdown: React.FC<DropdownProps> = ({
  title,
  items,
  isMobile = false,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledSetIsOpen || setInternalIsOpen;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const isActive = useMemo(() => items.some((item) => pathname === item.href), [items, pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, setIsOpen]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  if (isMobile) {
    return (
      <div className="px-2 py-3" ref={dropdownRef}>
        <button
          className={`flex items-center justify-between w-full px-3 py-2 text-base font-medium rounded-md ${
            isActive ? 'text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-slate-800' : 'text-gray-700 dark:text-gray-300'
          }`}
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {title}
          <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-2 pl-4 space-y-1"
            >
              {items.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`flex items-center px-3 py-2 text-base font-medium rounded-md ${
                    pathname === item.href
                      ? 'text-primary-600 dark:text-primary-400 bg-gray-50 dark:bg-slate-700'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon && <span className="mr-3">{item.icon}</span>}
                  {item.name}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
          isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
        }`}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {title}
        <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 z-50 backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95"
            role="menu"
          >
            {items.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                role="menuitem"
              >
                <Link
                  href={item.href}
                  className={`flex items-center px-4 py-2 text-sm ${
                    pathname === item.href
                      ? 'text-primary-600 dark:text-primary-400 bg-gray-50 dark:bg-slate-700/50'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon && (
                    <motion.span className="mr-3" whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                      {item.icon}
                    </motion.span>
                  )}
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Composant Header
const Header: React.FC = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();

  // Événement de défilement instantané (pas de debounce)
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > lastScrollY + 10) {
      setScrollDirection('down');
    } else if (currentScrollY < lastScrollY - 10) {
      setScrollDirection('up');
    }
    setScrolled(currentScrollY > 10);
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gérer le focus sur le champ de recherche
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
  }, []);

  // Ne pas afficher la barre de navigation sur les pages du dashboard
  const isDashboardPage = pathname && pathname.startsWith('/dashboard');
  
  if (isDashboardPage) {
    return null;
  }
  
  return (
    <>
      <motion.header
        ref={headerRef}
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-md' : 'bg-white dark:bg-slate-900 shadow-sm'
        }`}
        initial={{ y: 0 }}
        animate={{
          y: scrollDirection === 'down' && scrolled && !isMobileMenuOpen && !activeDropdown ? -100 : 0,
        }}
        transition={{ duration: 0.3 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <motion.div
            className="flex items-center space-x-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link href="/" className="flex items-center relative group">
              <motion.img
                src="/images/logo-ctc.png"
                alt="Logo CTC"
                className="h-16 w-auto object-contain"
                whileHover={{ scale: 1.10 }}
                whileTap={{ scale: 0.95 }}
              />
              <motion.span
                className="absolute -bottom-1 left-0 h-0.5 bg-primary-600"
                initial={{ width: 0 }}
                whileHover={{ width: '100%' }}
                transition={{ duration: 0.3 }}
              />
            </Link>

            <nav className="hidden md:flex space-x-1" role="navigation" aria-label="Navigation principale">
              {Object.entries(NAV_ITEMS).map(([key, items]) => (
                <motion.div
                  key={key}
                  onMouseEnter={() => setActiveDropdown(key)}
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <NavDropdown
                    title={key.charAt(0).toUpperCase() + key.slice(1)}
                    items={items}
                    isOpen={activeDropdown === key}
                    setIsOpen={(open) => (open ? setActiveDropdown(key) : setActiveDropdown(null))}
                  />
                </motion.div>
              ))}
            </nav>
          </motion.div>

          <motion.div
            className="flex items-center space-x-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="hidden md:block relative">
              <AnimatePresence>
                {isSearchOpen ? (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute right-0 top-0 flex items-center"
                  >
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Rechercher..."
                      className="w-full py-1.5 px-3 pr-8 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 text-sm"
                      onBlur={handleSearchToggle}
                    />
                    <button
                      className="absolute right-2 text-gray-500 dark:text-gray-400"
                      onClick={handleSearchToggle}
                      aria-label="Fermer la recherche"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    className="p-1.5 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors"
                    onClick={handleSearchToggle}
                    aria-label="Ouvrir la recherche"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Search className="h-5 w-5" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            <AuthStatusIndicator />

            <motion.button
              className="md:hidden p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </motion.button>
          </motion.div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-800 shadow-lg"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="px-2 pt-2 pb-3 space-y-1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                {Object.entries(NAV_ITEMS).map(([key, items]) => (
                  <NavDropdown
                    key={key}
                    title={key.charAt(0).toUpperCase() + key.slice(1)}
                    items={items}
                    isMobile={true}
                  />
                ))}

                <div className="px-5 py-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      className="w-full py-2 px-3 pl-10 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800"
                    />
                    <Search className="h-5 w-5 absolute left-3 top-2.5 text-gray-500 dark:text-gray-400" />
                  </div>
                </div>

                <motion.div
                  className="px-5 py-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <AuthStatusIndicator />
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <div className="h-16" />
    </>
  );
};

export default Header;